import { DangerButton, Modal, PrimaryButton, toast } from '@systemeio/ui'
import { ContactSubscriptionsInterface } from 'modules/contacts/contact-page/types/contact-subscriptions-interface'
import { SubscriptionInterface } from 'modules/payment/subscriptions/types/subscription-interface'
import React, { useState } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUnsubscribe } from '../hooks/use-unsubscribe'
import { getPaymentProcessorUnsubscribeOptions } from '../utils/get-payment-processor-unsubscribe-options'

interface UnsubscribeModalProps<
  Instance extends SubscriptionInterface | ContactSubscriptionsInterface,
> {
  opened: boolean
  onClose: () => void
  subscription: Instance
  onUnsubscribe: (subscription: Instance) => Promise<void>
}

function UnsubscribeModal<Instance extends SubscriptionInterface | ContactSubscriptionsInterface>({
  opened,
  onClose,
  subscription,
  onUnsubscribe,
}: UnsubscribeModalProps<Instance>) {
  const { t } = useLocoTranslation()
  const [error, setError] = useState('')
  const { requestMethod, getRequestUrl, getRequestBody } = getPaymentProcessorUnsubscribeOptions(
    subscription.paymentProcessor,
    subscription.id,
    subscription.subscriptionItems,
  )
  const { unsubscribe, isFetchingWhenEnds, isFetchingNow } = useUnsubscribe({
    requestMethod: requestMethod,
    getRequestUrl: getRequestUrl,
    getRequestBody,
  })
  const unsubWhenEnds = async () => {
    try {
      await unsubscribe({
        time: 'when_ends',
      })
      await onUnsubscribe(subscription)
      onClose()
      toast.success(t('dashboard.subscriptions.unsubscribed_later'))
    } catch (e) {
      toast.error(t('global.error'))
    }
  }
  const unsubNow = async () => {
    try {
      await unsubscribe({
        time: 'now',
      })
      await onUnsubscribe(subscription)
      onClose()
      toast.success(t('dashboard.subscriptions.unsubscribed'))
    } catch (e) {
      toast.error(t('global.error'))
    }
  }
  return (
    <Modal
      opened={opened}
      onClose={() => {
        onClose()
      }}
      className={'max-w-max'}
      afterLeave={() => setError('')}
      title={t('dashboard.subscriptions.unsubscribe_confirm')}
      isFetching={isFetchingWhenEnds || isFetchingNow}
      isCentered={true}
    >
      <form className="flex flex-col gap-6 lg:gap-10">
        <div className="flex justify-center">
          <p>
            <span className="text-text-primary text-base">
              <TransLoco
                t={t}
                i18nKey={'dashboard.subscriptions.unsubscribe_confirm_text'}
                values={{
                  product: subscription.subscriptionItems[0].orderItem.pricePlan?.innerName,
                  customer: subscription.customer.fullName,
                }}
                components={{ b: <span className="font-bold" /> }}
              />
            </span>
          </p>
        </div>
        <div className="flex justify-center gap-4 max-sm:flex-wrap">
          <DangerButton
            isFetching={isFetchingNow}
            width="large"
            onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              await unsubNow()
            }}
            disabled={isFetchingWhenEnds}
          >
            {t('dashboard.subscriptions.unsub_now')}
          </DangerButton>
          <PrimaryButton
            isFetching={isFetchingWhenEnds}
            className="w-full text-base"
            width="large"
            onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              await unsubWhenEnds()
            }}
            disabled={isFetchingNow}
            type="submit"
          >
            {t('dashboard.subscriptions.unsub_period')}
          </PrimaryButton>
        </div>
        <FieldErrorAndDescription error={error} errorClassName={'text-center'} />
      </form>
    </Modal>
  )
}

export default UnsubscribeModal
